import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * EXCLUSÃO DE CONTA — o lado do cliente.
 *
 * ─── O DEFEITO (D13), EM DUAS CAMADAS ───────────────────────────────────────
 *
 * A tela apagava dado de negócio com nove deletes seguidos e chamava
 * `signOut()`. Duas coisas estavam erradas ao mesmo tempo:
 *
 *   1. **A tela mentia.** `profiles` não tem política de DELETE: sob RLS o
 *      comando remove ZERO linhas e devolve **200 sem erro nenhum**. Medido
 *      contra produção. O guard que o D6/#109 deixou — `if (error) throw` — é
 *      estruturalmente cego para isso, porque não há erro.
 *   2. **A conta nunca morria.** Não existia `auth.admin.deleteUser` em lugar
 *      nenhum do repositório: o usuário de `auth.users` continuava lá e a pessoa
 *      entrava de novo com o mesmo e-mail e senha.
 *
 * ─── ONDE A EXCLUSÃO MORA HOJE ──────────────────────────────────────────────
 *
 * Na edge function `delete-account`, que roda com `service_role`. Apagar
 * `auth.users` exige credencial administrativa, que só existe no servidor — e,
 * de quebra, resolve o problema estrutural: `profiles.id` referencia
 * `auth.users(id)` ON DELETE CASCADE e todo o resto pendura em `auth.users` ou
 * em `companies` (também CASCADE), então a árvore inteira cai numa TRANSAÇÃO
 * só. Não há meio-estado — que era o risco dos nove deletes em sequência, a
 * origem do #109.
 *
 * ─── O QUE ESTE MÓDULO GUARDA ───────────────────────────────────────────────
 *
 * A regra que sobrevive a qualquer mudança de mecanismo: **não anuncie sucesso
 * sem prova**. Quem tem o veredito é o CORPO da resposta (#9) — a função
 * verifica no servidor que o perfil sumiu e só então responde `deleted: true`.
 * Um `invoke` que não estourou não é prova de nada.
 */

/**
 * A exclusão não foi confirmada pelo servidor. Erro próprio (e não um `Error`
 * genérico) porque a tela precisa de uma mensagem diferente da falha comum.
 */
export class AccountDeletionNotConfirmedError extends Error {
  constructor(public readonly step: string) {
    super(`exclusão não confirmada pelo servidor em: ${step}`);
    this.name = 'AccountDeletionNotConfirmedError';
  }
}

/**
 * Mensagem para quando a exclusão não se confirma.
 *
 * Ela afirma que NADA foi apagado, e isso é verdade por construção: a exclusão
 * é uma transação única no servidor — ou tudo morre, ou nada morre. Enquanto a
 * limpeza acontecia no cliente, em nove passos, essa frase teria sido falsa.
 */
export const ACCOUNT_DELETION_UNCONFIRMED_MESSAGE =
  'Não foi possível excluir sua conta e nada foi apagado. Tente de novo ou escreva para contato@orcamigo.com.br.';

/** O que a edge function devolve no caminho feliz. */
interface DeleteAccountResponse {
  deleted?: boolean;
}

/**
 * Pede a exclusão da conta ao servidor.
 *
 * `confirmEmail` é a mesma confirmação que a tela exige, repetida no servidor
 * como trava de INTENÇÃO — não como autenticação (a identidade sai do token, o
 * e-mail não é segredo). Numa ação irreversível, um POST acidental não pode
 * apagar a conta só por carregar o token.
 *
 * Lança `AccountDeletionNotConfirmedError` quando o servidor responde sem
 * confirmar. Repare que o `invoke` NÃO é o único juiz: ele só reclama de
 * status não-2xx, e um 200 sem `deleted: true` continua sendo uma exclusão que
 * não aconteceu (#9).
 */
export async function requestAccountDeletion(
  supabase: Pick<SupabaseClient, 'functions'>,
  confirmEmail: string,
): Promise<void> {
  const { data, error } = await supabase.functions.invoke<DeleteAccountResponse>(
    'delete-account',
    { body: { confirm_email: confirmEmail.trim() } },
  );

  if (error) throw error;
  if (!data?.deleted) throw new AccountDeletionNotConfirmedError('delete-account');
}
